import React, { useState, useRef, useEffect } from 'react';
import { updateRiskModel, saveRiskModel, trackRiskModelChange } from 'admin/riskmodels/modules/riskmodels';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useTheme } from 'emotion-theming';
import { Row, Col, Card, Typography, Form } from 'antd';
import { Label as Input, Button, BasicModal as Modal } from '@luxe/components';
import TotalCounter from './TotalCounter';

const RiskModelManagement = ({
  item,
  riskModels,
  saveRiskModel,
  trackRiskModelChange,
  isEditable,
  showWeightedScoreElement,
}) => {
  const { space, fontSizes, colors, itemAlignment } = useTheme();
  const [form] = Form.useForm();
  const { Text } = Typography;
  const [visible, setVisible] = useState(false);
  const [name, setName] = useState(item.name);
  const pendingSaveNameRef = useRef(null);

  const savedRiskModelName = riskModels?.RiskModel?.name;

  useEffect(() => {
    if (visible && pendingSaveNameRef.current && savedRiskModelName === pendingSaveNameRef.current) {
      setVisible(false);
      pendingSaveNameRef.current = null;
    }
  }, [visible, savedRiskModelName]);

  useEffect(() => {
    if (visible) {
      setName(item.name);
    }
  }, [visible, item.name]);

  const hasNameChanged = (name ?? '').trim() !== (item.name ?? '').trim() && (name ?? '').trim().length > 0;

  const updateName = () => {
    const values = form.getFieldsValue();
    const newName = values.name?.trim();
    if (newName) {
      pendingSaveNameRef.current = newName;
      saveRiskModel({ ...item, name: newName });
    }
  };

  return (
    <>
      <Row id="model-profile-management" style={{ marginBottom: `${space.xsmall}px` }}>
        <Card
          style={{
            width: '100%',
            margin: '15px',
            marginRight: 0,
            minHeight: '48px',
            backgroundColor: colors.BG1,
          }}
          bordered={false}
          title={
            <Row align="middle" gutter={16}>
              <Col style={{ ...itemAlignment.center, justifyContent: 'normal', width: 'auto', height: '48px' }}>
                <Text style={{ fontSize: `${fontSizes.heading}px`, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {item.name}
                </Text>
              </Col>
              <Col>
                {isEditable && (
                  <Button
                    variant="link"
                    type="link"
                    onClick={() => {
                      setVisible(!visible);
                    }}
                  >
                    Edit
                  </Button>
                )}
              </Col>
            </Row>
          }
          extra={
            <Row align="middle" style={{ width: '25vw' }}>
              <Col span={6} style={{ left: '60px' }}>
                {showWeightedScoreElement() && (
                  <TotalCounter
                    total={
                      riskModels.RiskModel.indices &&
                      riskModels.RiskModel.indices.length &&
                      riskModels.RiskModel.indices
                        .map(x => x.weight)
                        .reduce((total, weight) => parseInt(total) + parseInt(weight))
                    }
                  />
                )}
              </Col>
            </Row>
          }
          headStyle={{
            border: 'none',
            lineHeight: '28px',
            fontSize: '20px',
            padding: '0 15px',
          }}
          bodyStyle={{ display: 'none' }}
        />
      </Row>
      <Modal
        open={visible}
        title="Edit Risk Model Label"
        okText="Save"
        cancelText="Cancel"
        okButtonProps={{ disabled: !hasNameChanged }}
        onCancel={() => {
          pendingSaveNameRef.current = null;
          setVisible(false);
          setName(item.name);
          form.resetFields();
        }}
        onOk={() => {
          form
            .validateFields()
            .then(() => {
              updateName();
            })
            .catch(info => {
              console.error('Validate Failed:', info);
            });
        }}
      >
        <Form
          form={form}
          layout="vertical"
          name="edit_risk_model_label_form"
          initialValues={{
            modifier: 'public',
            name: item.name,
          }}
        >
          <Form.Item
            name="name"
            label={<label style={{ color: 'white' }}>Name</label>}
            rules={[
              {
                required: true,
                message: 'Please input the title of the Risk Model!',
              },
            ]}
          >
            <Input
              onChange={e => {
                setName(e.target.value);
                trackRiskModelChange(true);
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

function mapStateToProps({ riskModels, router }) {
  return {
    riskModels,
    location: router.location,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      updateRiskModel,
      saveRiskModel,
      trackRiskModelChange,
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(RiskModelManagement);
